import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { DateTime } from 'luxon';
import { Text, HeaderText } from '@el8/vital';
import { Card } from 'components';

import { getBookingInfo } from 'modules/booking';
import { getAppointmentType } from 'modules/appointmentType';
import { getPhysician } from 'modules/physician';
import { getPractice } from 'modules/practice';
import { getServiceLocation } from 'modules/serviceLocations';
import { formatPhone } from 'utils';
import { formatPracticeAddress, getPracticePhone } from 'utils/practice';
import { formatPhysicianName } from 'utils/physician';
import { humanizeDate } from 'utils/dates';

import { PracticeInfo, PhysicianInfo, State, ServiceLocationInfo } from 'types';
import { AppointmentType } from 'EntityTypes';

import styles from './AppointmentSummary.less';

type OwnProps = {
  practiceId: number;
};

type StateProps = {
  appointmentType: AppointmentType;
  physician: PhysicianInfo;
  practice?: PracticeInfo;
  scheduledDate: string;
  serviceLocation?: ServiceLocationInfo;
  timezone: string;
};

type Props = OwnProps & StateProps;

const mapStateToProps = (state: State, props: OwnProps): StateProps => {
  const { physicianId, appointmentTypeId, ...bookingInfo } = getBookingInfo(state);
  const isAvailabilityOnBkSiteEnabled = window.el8Globals.FEATURES.AvailabilityOnBkSite;
  return {
    ...bookingInfo,
    appointmentType: getAppointmentType(state, { appointmentTypeId }),
    physician: getPhysician(state, { physicianId }),
    practice: getPractice(state, props),
    serviceLocation: isAvailabilityOnBkSiteEnabled
      ? getServiceLocation(state, bookingInfo.selectedServiceLocationId)
      : null,
  };
};

const AppointmentSummary: React.FC<Props> = ({
  scheduledDate,
  appointmentType,
  physician,
  practice,
  serviceLocation,
  timezone,
}) => {
  const practicePhone = useMemo(() => getPracticePhone(practice), [practice]);
  const tz = window.el8Globals.FEATURES.SchedulingTimeZone ? timezone : 'local';

  const isAvailabilityOnBkSiteEnabled = window.el8Globals.FEATURES.AvailabilityOnBkSite;
  const serviceLocationPhone =
    isAvailabilityOnBkSiteEnabled && serviceLocation ? formatPhone(serviceLocation.phone) : null;

  return (
    <Card>
      <HeaderText
        align="center"
        className={styles.service}
        legacyVariant="legacyHeaderText"
        tag="h1"
      >{`${appointmentType.name} with ${formatPhysicianName(physician)}`}</HeaderText>
      <HeaderText
        align="center"
        style={{ marginBottom: 16 }}
        legacyVariant="legacyTitleText"
        sizing="heading-lg"
        tag="h1"
      >
        {`${humanizeDate(scheduledDate)} ${DateTime.fromISO(scheduledDate)
          .setZone(tz)
          .toFormat('@ h:mm a ZZZZ')}`}
      </HeaderText>
      <HeaderText
        align="center"
        className={styles.service}
        legacyVariant="legacyHeaderText"
        tag="h1"
      >
        {serviceLocation?.name || practice.name}
      </HeaderText>
      <Text align="center" appearance="system" tag="div">
        {isAvailabilityOnBkSiteEnabled
          ? `${serviceLocation.fullAddress}`
          : formatPracticeAddress(practice)}
      </Text>
      {(serviceLocationPhone || practicePhone) && (
        <Text align="center" appearance="system" tag="div">
          {serviceLocationPhone || practicePhone}
        </Text>
      )}
    </Card>
  );
};

export default connect(mapStateToProps)(AppointmentSummary);
